// src/services/iibbService.js
/**
 * IIBB Service - Handles Ingresos Brutos (ARBA/AGIP) lookups
 * 
 * NOTE: Uses axios (not fetch) for automatic token refresh via AuthProvider interceptors
 */
import axios from 'axios';
import { NODE_BACKEND_API_URL } from '../config';

/**
 * Check ARBA padrón for Ingresos Brutos retention/perception rates
 * @param {string} cuit - CUIT number to check (with or without dashes)
 * @param {string} documentType - Document type ('CUIT' or 'DNI'), defaults to 'CUIT'
 * @returns {Promise<Object>} ARBA padrón data with retention and perception rates
 */
export const checkARBAPadron = async (cuit, documentType = 'CUIT') => {
    // Clean CUIT/DNI (remove dashes if present)
    const cleanDocument = cuit.replace(/-/g, '');
    
    try {
        console.log('[iibbService] Checking ARBA padrón for:', cleanDocument);

        const response = await axios.post(
            `${NODE_BACKEND_API_URL}/clients/check-arba-padron`,
            {
                documento_nro: cleanDocument,
                documento_tipo: documentType
            }
        );

        const data = response.data;
        console.log('[iibbService] ARBA padrón check result:', data);
        
        return {
            source: 'ARBA',
            existsInPadron: data.existe_padron === 'S',
            perceptionRate: data.alicuota_percepcion || 0,
            retentionRate: data.alicuota_retencion || 0,
            hasError: data.error === 'S',
            errors: data.errores || [],
            rawResponse: data
        };

    } catch (error) {
        console.error('[iibbService] Error checking ARBA padrón:', error);
        const errorMessage = error.response?.data?.message || error.message;
        throw new Error(errorMessage);
    }
};

/**
 * Check AGIP padrón for Ingresos Brutos retention/perception rates (Ciudad de Buenos Aires)
 * @param {string} cuit - CUIT number to check (with or without dashes)
 * @param {string} documentType - Document type (only 'CUIT' is supported by AGIP), defaults to 'CUIT'
 * @returns {Promise<Object>} AGIP padrón data with retention and perception rates
 */
export const checkAGIPPadron = async (cuit, documentType = 'CUIT') => {
    // Clean CUIT (remove dashes if present)
    const cleanDocument = cuit.replace(/-/g, '');
    
    try {
        console.log('[iibbService] Checking AGIP padrón for:', cleanDocument);

        const response = await axios.post(
            `${NODE_BACKEND_API_URL}/clients/check-agip-padron`,
            {
                documento_nro: cleanDocument,
                documento_tipo: documentType
            }
        );

        const data = response.data;
        console.log('[iibbService] AGIP padrón check result:', data);
        
        return {
            source: 'AGIP',
            existsInPadron: data.existe_padron === 'S',
            perceptionRate: data.alicuota_percepcion || 0,
            retentionRate: data.alicuota_retencion || 0,
            hasError: data.error === 'S',
            errors: data.errores || [],
            rawResponse: data
        };

    } catch (error) {
        console.error('[iibbService] Error checking AGIP padrón:', error);
        const errorMessage = error.response?.data?.message || error.message;
        throw new Error(errorMessage);
    }
};

/**
 * Check if a CUIT requires Ingresos Brutos retention/perception in both ARBA and AGIP
 * Returns combined data from both padrones
 * @param {string} cuit - CUIT number to check
 * @returns {Promise<Object>} Combined IIBB data from ARBA and AGIP
 */
export const requiresIIBBRetention = async (cuit) => {
    try {
        console.log('[iibbService] Checking IIBB in both ARBA and AGIP for:', cuit);
        
        // Check both padrones in parallel
        const [arbaResult, agipResult] = await Promise.allSettled([
            checkARBAPadron(cuit),
            checkAGIPPadron(cuit)
        ]);
        
        const arbaData = arbaResult.status === 'fulfilled' ? arbaResult.value : null;
        const agipData = agipResult.status === 'fulfilled' ? agipResult.value : null;
        
        // Determine which padrón has data
        const hasARBA = arbaData && arbaData.existsInPadron;
        const hasAGIP = agipData && agipData.existsInPadron;
        
        // Use ARBA if available, otherwise AGIP
        const primaryData = hasARBA ? arbaData : hasAGIP ? agipData : null;
        
        if (!primaryData) {
            return {
                requiresRetention: false,
                requiresPerception: false,
                retentionRate: 0,
                perceptionRate: 0,
                existsInPadron: false,
                source: null,
                arbaChecked: arbaResult.status === 'fulfilled',
                agipChecked: agipResult.status === 'fulfilled',
                arbaData: arbaData,
                agipData: agipData
            };
        }
        
        return {
            requiresRetention: primaryData.retentionRate > 0,
            requiresPerception: primaryData.perceptionRate > 0,
            retentionRate: primaryData.retentionRate,
            perceptionRate: primaryData.perceptionRate,
            existsInPadron: true,
            source: primaryData.source,
            arbaChecked: arbaResult.status === 'fulfilled',
            agipChecked: agipResult.status === 'fulfilled',
            arbaData: arbaData,
            agipData: agipData,
            hasARBA: hasARBA,
            hasAGIP: hasAGIP
        };
    } catch (error) {
        console.error('[iibbService] Error checking IIBB retention requirement:', error);
        // Return safe defaults on error
        return {
            requiresRetention: false,
            requiresPerception: false,
            retentionRate: 0,
            perceptionRate: 0,
            existsInPadron: false,
            source: null,
            error: error.message
        };
    }
};

/**
 * Calculate IIBB retention amount based on invoice total
 * @param {number} invoiceTotal - Total amount of the invoice
 * @param {number} retentionRate - Retention rate percentage (e.g., 3.5 for 3.5%)
 * @returns {number} Retention amount
 */
export const calculateIIBBRetention = (invoiceTotal, retentionRate) => {
    if (!invoiceTotal || !retentionRate || retentionRate <= 0) {
        return 0;
    }
    
    return (invoiceTotal * retentionRate) / 100;
};

/**
 * Calculate IIBB perception amount based on invoice total
 * @param {number} invoiceTotal - Total amount of the invoice
 * @param {number} perceptionRate - Perception rate percentage (e.g., 3 for 3%)
 * @returns {number} Perception amount
 */
export const calculateIIBBPerception = (invoiceTotal, perceptionRate) => {
    if (!invoiceTotal || !perceptionRate || perceptionRate <= 0) {
        return 0;
    }
    
    return (invoiceTotal * perceptionRate) / 100;
};

/**
 * Get formatted IIBB status message for UI display
 * @param {Object} iibbData - Data from requiresIIBBRetention
 * @returns {string} Formatted message
 */
export const getIIBBStatusMessage = (iibbData) => {
    if (!iibbData.existsInPadron) {
        return 'No está sujeto a retención/percepción de IIBB';
    }
    
    const messages = [];
    const source = iibbData.source || 'IIBB';
    
    if (iibbData.requiresRetention) {
        messages.push(`Retención: ${iibbData.retentionRate}%`);
    }
    
    if (iibbData.requiresPerception) {
        messages.push(`Percepción: ${iibbData.perceptionRate}%`);
    }
    
    if (messages.length === 0) {
        return `En padrón ${source} con alícuota 0% (consulte con contador)`;
    }
    
    return `${source}: ${messages.join(' | ')}`;
};

/**
 * Get detailed IIBB status message including both ARBA and AGIP if applicable
 * Provides buyer-focused guidance for purchase orders
 * @param {Object} iibbData - Data from requiresIIBBRetention
 * @param {string} context - 'purchase' for buying (orders), 'sale' for selling (invoices), defaults to 'sale'
 * @returns {Object} { primary: string, details: string[], warning: string, buyerGuidance: string[] }
 */
export const getDetailedIIBBStatus = (iibbData, context = 'sale') => {
    const details = [];
    const buyerGuidance = [];
    let warning = null;
    
    if (!iibbData.existsInPadron) {
        return {
            primary: 'No está sujeto a retención/percepción de IIBB',
            details: [],
            warning: null,
            buyerGuidance: []
        };
    }
    
    // For purchase orders (you're buying from supplier)
    const isPurchase = context === 'purchase';
    
    // Check ARBA
    if (iibbData.hasARBA && iibbData.arbaData) {
        const arbaMessages = [];
        if (iibbData.arbaData.retentionRate > 0) {
            arbaMessages.push(`Retención: ${iibbData.arbaData.retentionRate}%`);
            if (isPurchase) {
                buyerGuidance.push(`🔹 Retención ARBA ${iibbData.arbaData.retentionRate}%: Aplicar SOLO si USTED está registrado en Provincia de Buenos Aires`);
            }
        }
        if (iibbData.arbaData.perceptionRate > 0) {
            arbaMessages.push(`Percepción: ${iibbData.arbaData.perceptionRate}%`);
            if (isPurchase) {
                buyerGuidance.push(`⚠️ El proveedor debería cobrarle ${iibbData.arbaData.perceptionRate}% percepción ARBA (proveedor en Prov. Bs. As.)`);
            }
        }
        if (arbaMessages.length > 0) {
            details.push(`ARBA (Prov. Bs. As.): ${arbaMessages.join(', ')}`);
        } else {
            details.push('ARBA (Prov. Bs. As.): Alícuota 0%');
            if (isPurchase) {
                buyerGuidance.push('✓ Retención ARBA: No aplicar (alícuota 0%)');
            }
            warning = 'Alícuota 0% - Consulte con contador sobre aplicabilidad';
        }
    }
    
    // Check AGIP
    if (iibbData.hasAGIP && iibbData.agipData) {
        const agipMessages = [];
        if (iibbData.agipData.retentionRate > 0) {
            agipMessages.push(`Retención: ${iibbData.agipData.retentionRate}%`);
            if (isPurchase) {
                buyerGuidance.push(`🔹 Retención AGIP ${iibbData.agipData.retentionRate}%: Aplicar SOLO si USTED está registrado en CABA`);
            }
        }
        if (iibbData.agipData.perceptionRate > 0) {
            agipMessages.push(`Percepción: ${iibbData.agipData.perceptionRate}%`);
            if (isPurchase) {
                buyerGuidance.push(`⚠️ El proveedor debería cobrarle ${iibbData.agipData.perceptionRate}% percepción AGIP (proveedor en CABA)`);
            }
        }
        if (agipMessages.length > 0) {
            details.push(`AGIP (CABA): ${agipMessages.join(', ')}`);
        } else {
            details.push('AGIP (CABA): Alícuota 0%');
            if (isPurchase) {
                buyerGuidance.push('✓ Retención AGIP: No aplicar (alícuota 0%)');
            }
            warning = 'Alícuota 0% - Consulte con contador sobre aplicabilidad';
        }
    }
    
    // Add inter-jurisdictional note for purchases
    if (isPurchase && (iibbData.hasARBA || iibbData.hasAGIP)) {
        buyerGuidance.push('ℹ️ Operación inter-jurisdiccional: Consulte con contador sobre percepciones aplicables');
    }
    
    const primary = iibbData.requiresRetention || iibbData.requiresPerception
        ? `Sujeto a IIBB - ${iibbData.source}`
        : `En padrón ${iibbData.source} con alícuota 0%`;
    
    return {
        primary,
        details,
        warning,
        buyerGuidance
    };
};
